// AddCartonPdfOverlay.jsx — Phase 2 Step B
//
// The vector-only add-carton flow (D3=A). Drop one or more carton PDFs; for each
// the backend detects the die WITHOUT nesting (POST /extract/detect-die) and runs
// a color preflight (POST /export/preflight). The user sees a live die preview +
// CMYK/RGB result per file, sets copies, then commits the batch to the layout.
//
// This component is presentational + detection only. Committing (appending to the
// carton list, registering SKUs) is handled by the parent via onCommit, so all
// state that drives the layout stays in App.

import React from 'react';
import { useTheme } from './theme';
import { detectDie, PlannerApiError } from './planner';
import { runPreflight, VectorApiError } from './exportVector';
import { describeDieRefusal } from './platformErrors';
import { useAuth } from './AuthProvider';
import { listCartons, getCarton, getCartonSource, cartonDims, LibraryApiError, browseCartons } from './library';
import { isSuperuser, libraryOnly, ownerLine, viewLabel } from './superuserLibrary';
import DiePreview from './DiePreview';
import { fileDigest } from './benchMerge';
// PR ⑬b (D4=c): offer the prepress tag at drop time, where the operator is
// already looking at the detected outline.
import { unflappedBlockReason } from './prepress';

const uid = (name) => `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const SU_LOAD_PAGE = 200;   // the backend's largest page

export default function AddCartonPdfOverlay({ open, onClose, onCommit, apiBase, caps }) {
  const { P, FN, MO, DISPLAY, fmtLen } = useTheme();
  const [mode, setMode] = React.useState('upload');   // 'upload' | 'library'
  const [staged, setStaged] = React.useState([]);
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  // Library browse state (loaded lazily when the tab is opened).
  const [lib, setLib] = React.useState(null);         // null = not loaded / loading
  const [libErr, setLibErr] = React.useState(null);
  const [libQ, setLibQ] = React.useState('');
  const [loadingId, setLoadingId] = React.useState(null);
  // V2 #97: a superuser's list follows their Library's view (the backend
  // remembers it; it is changed in the Library) and is searched on the server.
  const { user } = useAuth();
  const su = isSuperuser(user);
  const [libView, setLibView] = React.useState(null);   // { view, orgsById, more }
  const [libQSent, setLibQSent] = React.useState('');

  React.useEffect(() => { if (!open) { setStaged([]); setMode('upload'); setLib(null); setLibErr(null); setLibQ(''); setLibView(null); } }, [open]);

  React.useEffect(() => {
    if (!su) return undefined;
    const t = setTimeout(() => setLibQSent(libQ.trim()), 300);
    return () => clearTimeout(t);
  }, [libQ, su]);

  React.useEffect(() => {
    if (!su || !open || mode !== 'library') return undefined;
    let live = true;
    (async () => {
      try {
        const body = await browseCartons({ apiBase, filters: { q: libQSent }, pageSize: SU_LOAD_PAGE });
        if (!live) return;
        const items = body.items || [];
        setLib(libraryOnly(items));
        setLibView({
          view: body.view,
          orgsById: Object.fromEntries(items.map((c) => [c.org_id, { name: c.org_name }])),
          more: body.total > items.length,
        });
      } catch (e) { if (live) { setLibErr(e instanceof LibraryApiError ? e.message : String(e)); setLib([]); } }
    })();
    return () => { live = false; };
  }, [su, open, mode, apiBase, libQSent]);

  React.useEffect(() => {
    if (su || !open || mode !== 'library' || lib !== null) return;
    let live = true;
    (async () => {
      try { const list = await listCartons({ apiBase }); if (live) setLib(list); }
      catch (e) { if (live) { setLibErr(e instanceof LibraryApiError ? e.message : String(e)); setLib([]); } }
    })();
    return () => { live = false; };
  }, [su, open, mode, lib, apiBase]);

  const loadFromLibrary = async (c) => {
    if (c.has_source === false || c.source_kind === 'parametric') return;
    setLoadingId(c.id);
    try {
      let full = c;
      try { full = await getCarton({ apiBase, id: c.id }); } catch { /* metric best-effort */ }
      const filename = full.geometry?.filename || full.filename || `${full.name || full.id}.pdf`;
      const file = await getCartonSource({ apiBase, id: full.id, filename });
      onCommit([{ file, name: filename, count: 1, detection: full.geometry, decision: undefined }]);
      onClose();
    } catch (e) {
      setLibErr(e instanceof LibraryApiError ? e.message : String(e));
    } finally { setLoadingId(null); }
  };

  const patch = (id, fields) => setStaged((prev) => prev.map((it) => (it.id === id ? { ...it, ...fields } : it)));

  const addFiles = async (fileList) => {
    const pdfs = Array.from(fileList || []).filter((f) => /\.pdf$/i.test(f.name));
    if (!pdfs.length) return;
    // The same file twice is one carton with more copies, not two rows
    // (benchMerge.js does the same on the bench). Compared by bytes.
    const digests = await Promise.all(pdfs.map(fileDigest));
    const items = [];
    const extra = {};                             // staged/new row id → copies to add
    pdfs.forEach((file, k) => {
      const digest = digests[k];
      const twin = digest && (staged.find((it) => it.digest === digest)
        || items.find((it) => it.digest === digest));
      if (twin) { extra[twin.id] = (extra[twin.id] || 0) + 1; return; }
      items.push({
        id: uid(file.name), file, digest, name: file.name, copies: 1,
        detecting: true, detection: null, detErr: null,
        preflighting: true, preflight: null, pfErr: null, decision: undefined,
      });
    });
    const bump = (it) => (extra[it.id] ? { ...it, copies: it.copies + extra[it.id] } : it);
    setStaged((prev) => [...prev.map(bump), ...items.map(bump)]);

    items.forEach(async (it) => {
      // Die detection (no nest).
      try {
        const { results } = await detectDie({ apiBase, files: [it.file] });
        const d = (results || [])[0] || null;
        if (d && Array.isArray(d.vertices) && d.vertices.length >= 3) patch(it.id, { detection: d, detecting: false });
        else patch(it.id, { detErr: 'No die found in this PDF.', detecting: false });
      } catch (e) {
        // A die the backend tried to read from the artwork's borders and
        // could not says why — which shapes, which gap, how many cartons.
        const refusal = e instanceof PlannerApiError ? describeDieRefusal(e.body) : null;
        const msg = refusal || (e instanceof PlannerApiError && e.status === 400
          ? 'No DIELINE layer or CutContour spot — re-export the artwork with the die convention.'
          : (e instanceof PlannerApiError ? e.message : String(e)));
        patch(it.id, { detErr: msg, detecting: false });
      }
      // Color preflight.
      try {
        const { results } = await runPreflight({ apiBase, files: [it.file] });
        patch(it.id, { preflight: (results || [])[0] || null, preflighting: false });
      } catch (e) {
        patch(it.id, { pfErr: e instanceof VectorApiError ? e.message : String(e), preflighting: false });
      }
    });
  };

  if (!open) return null;

  const addable = staged.filter((it) => it.detection && !it.detErr);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); };

  const commit = () => {
    if (!addable.length) return;
    onCommit(addable.map((it) => ({
      file: it.file, name: it.name, count: it.copies,
      detection: it.detection, decision: it.decision,
      prepress: it.prepress || null,
    })));
    setStaged([]);
    onClose();
  };

  const ghost = { fontFamily: MO, fontSize: 11, padding: '5px 11px', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}` };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(20,16,12,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        background: P.sheet, border: `0.5px solid ${P.rule}`, borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(20,16,12,.28)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 12px' }}>
          <div>
            <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent }}>Add carton</div>
            <div style={{ fontFamily: DISPLAY, fontSize: 24, color: P.ink, lineHeight: 1.1, marginTop: 2 }}>{mode === 'library' ? 'From library' : 'From PDF'}</div>
          </div>
          <button onClick={onClose} title="Close" style={{ ...ghost, fontSize: 16, padding: '2px 9px' }}>×</button>
        </div>
        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 2, padding: '0 18px 12px', borderBottom: `0.5px solid ${P.rule}` }}>
          {[{ k: 'upload', label: 'Upload PDF' }, { k: 'library', label: 'Load from library' }].map((t) => (
            <button key={t.k} onClick={() => setMode(t.k)} style={{
              fontFamily: MO, fontSize: 11, fontWeight: 500, padding: '6px 13px', borderRadius: 6, cursor: 'pointer', border: 'none',
              background: mode === t.k ? P.ink : 'transparent', color: mode === t.k ? P.sheet : P.muted,
            }}>{t.label}</button>
          ))}
        </div>

        {/* Body — Upload */}
        {mode === 'upload' && (
        <div style={{ padding: 18, overflowY: 'auto' }}>
          {/* Drop zone */}
          <div
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '22px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
              background: dragging ? P.accentSoft : P.elev,
              border: `1px dashed ${dragging ? P.accent : P.rule}`,
            }}>
            <div style={{ fontFamily: FN, fontSize: 13, fontWeight: 500, color: P.ink }}>Drop carton PDFs here, or click to browse</div>
            <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, lineHeight: 1.45, maxWidth: 380 }}>
              The die is detected automatically from a <code style={{ fontFamily: MO }}>DIELINE</code> layer
              or a <code style={{ fontFamily: MO }}>CutContour</code> spot color — no tracing.
            </div>
            <input ref={inputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>

          {/* Staged files */}
          {staged.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              {staged.map((it) => {
                const bbox = it.detection?.bbox_mm;
                const dims = bbox ? `${fmtLen(bbox.width ?? bbox.w ?? bbox[2] ?? 0)} × ${fmtLen(bbox.height ?? bbox.h ?? bbox[3] ?? 0)}` : null;
                const flagged = it.preflight?.has_rgb;
                return (
                  <div key={it.id} style={{ display: 'flex', gap: 12, padding: 12, borderRadius: 10, background: P.elev, border: `0.5px solid ${it.detErr ? P.dangerLine : P.ruleSoft}` }}>
                    {/* Preview */}
                    <div style={{ width: 116, height: 116, flexShrink: 0, borderRadius: 8, background: P.sheet, border: `0.5px solid ${P.ruleSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {it.detecting
                        ? <span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>Detecting…</span>
                        : it.detection
                          ? <DiePreview vertices={it.detection.vertices} P={P} />
                          : <span style={{ fontFamily: MO, fontSize: 18, color: P.danger }}>⚠</span>}
                    </div>
                    {/* Meta */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: MO, fontSize: 12, fontWeight: 500, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                        <button onClick={() => setStaged((p) => p.filter((x) => x.id !== it.id))} title="Remove"
                          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: P.dim, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>

                      {it.detErr ? (
                        <div style={{ fontFamily: FN, fontSize: 11, color: P.danger, lineHeight: 1.4 }}>{it.detErr}</div>
                      ) : it.detection ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontFamily: MO, fontSize: 10, color: P.muted }}>
                          {dims && <span style={chip(P)}>{dims}</span>}
                          {it.detection.die_source && <span style={chip(P)}>{it.detection.die_source}</span>}
                          {it.detection.is_rectangular != null && <span style={chip(P)}>{it.detection.is_rectangular ? 'rectangular' : 'irregular'}</span>}
                        </div>
                      ) : null}

                      {/* Preflight */}
                      <div style={{ marginTop: 2 }}>
                        {it.preflighting ? (
                          <span style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>Color preflight…</span>
                        ) : it.pfErr ? (
                          <span style={{ fontFamily: MO, fontSize: 10, color: P.warn }}>Preflight unavailable</span>
                        ) : it.preflight ? (
                          flagged ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={pill(P.warn, P.warnSoft)}>RGB detected</span>
                              <button onClick={() => patch(it.id, { decision: 'proceed' })} style={it.decision === 'proceed' ? pillActive(P, P.ok) : ghost}>Proceed</button>
                              <button onClick={() => patch(it.id, { decision: 'reexport' })} style={it.decision === 'reexport' ? pillActive(P, P.warn) : ghost}>I'll re-export</button>
                            </div>
                          ) : (
                            <span style={pill(P.ok, P.okSoft)}>CMYK ✓</span>
                          )
                        ) : null}
                      </div>

                      {/* Copies */}
                      {it.detection && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 4, fontFamily: MO, fontSize: 10.5, color: P.muted }}>
                          Copies
                          <input type="number" min="1" step="1" value={it.copies}
                            onChange={(e) => patch(it.id, { copies: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            style={{ fontFamily: MO, fontSize: 12, padding: '3px 6px', width: 56, borderRadius: 5, border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink, textAlign: 'center' }} />
                        </label>
                      )}

                      {/* PR ⑬b: prepress tag, offered only when the detected
                          outline is one of the unflapped topologies this
                          deployment takes. This is the moment the operator can
                          actually see the shape, so it is the cheapest place to
                          catch a finished dieline. ⑭k: the accepted counts come
                          from capabilities — 6 / 10 / 8 or 12 across the three
                          families — not from a literal.
                          Affordance only — the server's 400 is authoritative. */}
                      {it.detection && !unflappedBlockReason(it.detection.vertices, caps) && (
                        <label style={{
                          display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 2,
                          fontFamily: MO, fontSize: 10.5, color: it.prepress ? P.ink : P.muted, cursor: 'pointer',
                        }}>
                          <input
                            type="checkbox"
                            checked={!!it.prepress}
                            onChange={(e) => patch(it.id, { prepress: e.target.checked ? 'plain' : null })}
                            style={{ margin: 0, cursor: 'pointer' }}
                          />
                          Generate dieline (prepress)
                          <span style={{ color: P.dim }}>— adds flaps to this bare outline</span>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Body — Library */}
        {mode === 'library' && (
        <div style={{ padding: 18, overflowY: 'auto' }}>
          <input value={libQ} onChange={(e) => setLibQ(e.target.value)} placeholder="Search saved cartons…"
            style={{ fontFamily: MO, fontSize: 12, padding: '7px 10px', width: '100%', boxSizing: 'border-box', borderRadius: 6, border: `0.5px solid ${P.rule}`, background: P.sheet, color: P.ink, marginBottom: 12 }} />
          {libErr && (
            <div style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 12, background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`, fontFamily: FN, fontSize: 11.5, color: P.ink }}>{libErr}</div>
          )}
          {su && libView && (
            <div style={{ fontFamily: FN, fontSize: 11, color: P.dim, marginBottom: 10, lineHeight: 1.45 }}>
              From your Library’s view: <span style={{ color: P.muted, fontWeight: 600 }}>{viewLabel(libView.view, libView.orgsById)}</span>.
              {' '}Change it in the Library.{libView.more ? ` Showing the newest ${SU_LOAD_PAGE}; search to find others.` : ''}
            </div>
          )}
          {lib === null ? (
            <div style={{ fontFamily: MO, fontSize: 11, color: P.dim, textAlign: 'center', padding: '30px 0' }}>Loading library…</div>
          ) : (() => {
            // a superuser's list arrives searched; a member's is searched here
            const rows = su ? lib : lib.filter((c) => !libQ.trim() || `${c.name} ${c.filename || ''}`.toLowerCase().includes(libQ.trim().toLowerCase()));
            if (!rows.length) {
              return <div style={{ fontFamily: FN, fontSize: 12, color: P.dim, textAlign: 'center', padding: '30px 0', lineHeight: 1.5 }}>
                {lib.length === 0 ? 'No saved cartons yet. Upload one from the Upload tab, or in the Library workspace.' : 'No matches.'}
              </div>;
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((c) => {
                  const dims = cartonDims(c);
                  const noSrc = c.has_source === false || c.source_kind === 'parametric';
                  return (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderRadius: 8, background: P.elev, border: `0.5px solid ${P.ruleSoft}`, opacity: noSrc ? 0.55 : 1 }}>
                      <div style={{ width: 56, height: 56, flexShrink: 0, borderRadius: 6, background: P.sheet, border: `0.5px solid ${P.ruleSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DiePreview vertices={c.geometry?.vertices} size={50} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: MO, fontSize: 12, fontWeight: 500, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        <div style={{ fontFamily: MO, fontSize: 9.5, color: P.dim, marginTop: 2 }}>
                          {dims ? `${fmtLen(dims.w)} × ${fmtLen(dims.h)}` : ''}{c.visibility === 'org' ? ' · shared' : ''}{noSrc ? ' · no source PDF' : ''}{su && ownerLine(c) ? ` · ${ownerLine(c)}` : ''}
                        </div>
                      </div>
                      <button onClick={() => loadFromLibrary(c)} disabled={noSrc || loadingId === c.id} title={noSrc ? 'No source PDF — cannot load for export' : 'Add this carton'} style={{
                        fontFamily: MO, fontSize: 10.5, padding: '6px 12px', borderRadius: 6, border: 'none', flexShrink: 0,
                        cursor: (noSrc || loadingId === c.id) ? 'default' : 'pointer',
                        background: (noSrc || loadingId === c.id) ? P.surface : P.accent, color: (noSrc || loadingId === c.id) ? P.dim : '#fff',
                      }}>{loadingId === c.id ? 'Loading…' : 'Add'}</button>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
        )}

        {/* Footer (upload mode) */}
        {mode === 'upload' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: `0.5px solid ${P.rule}` }}>
          <button onClick={onClose} style={ghost}>Cancel</button>
          <button onClick={commit} disabled={!addable.length} style={{
            fontFamily: FN, fontSize: 12.5, fontWeight: 500, padding: '8px 16px', borderRadius: 6, letterSpacing: 0.1,
            border: 'none', cursor: addable.length ? 'pointer' : 'default',
            background: addable.length ? P.ink : P.surface, color: addable.length ? P.sheet : P.dim,
          }}>
            {addable.length ? `Add ${addable.length} carton${addable.length > 1 ? 's' : ''}` : 'Add cartons'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

function chip(P) {
  return { padding: '2px 7px', borderRadius: 4, background: P.surface, border: `0.5px solid ${P.ruleSoft}` };
}
function pill(color, bg) {
  return { fontFamily: '"Geist Mono", monospace', fontSize: 9.5, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color, background: bg, padding: '3px 8px', borderRadius: 4 };
}
function pillActive(P, color) {
  return { fontFamily: '"Geist Mono", monospace', fontSize: 10, padding: '4px 9px', borderRadius: 6, cursor: 'pointer', background: color, color: '#fff', border: 'none' };
}
